import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AssessmentRef } from '../../utils/refs';
import { deleteField, setDoc } from 'firebase/firestore';
import { getCurrentTimestamp, captureError } from '../../utils';
import { writeAssessmentActivityLog } from './useUpdateAssessmentActivityLog';

const removeMentalityAssessmentResponse = async (assessmentID, questionID) => {
  const docRef = AssessmentRef(assessmentID);

  await setDoc(
    docRef,
    {
      responses: {
        [questionID]: deleteField()
      },
      lastModified: getCurrentTimestamp()
    },
    { merge: true }
  );

  return { questionID };
};

export default function useRemoveMentalityAssessmentResponse(assessmentID) {
  const queryClient = useQueryClient();
  const queryKey = ['mentalityAssessment', assessmentID];

  const RemoveMentalityAssessmentResponse = ({ questionID }) => {
    return removeMentalityAssessmentResponse(assessmentID, questionID);
  };

  return useMutation({
    mutationFn: RemoveMentalityAssessmentResponse,
    onMutate: async ({ questionID }) => {
      await queryClient.cancelQueries({ queryKey });

      const prevData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (assessment) => {
        if (!assessment) {
          return assessment;
        }

        const responses = { ...assessment.responses };
        delete responses[questionID];

        return { ...assessment, responses };
      });

      return { prevData };
    },
    onError: (error, variables, context) => {
      captureError(error, { assessmentID, ...variables, write: 'removeResponse' });
      writeAssessmentActivityLog(assessmentID, variables?.questionID, 'responseWriteFailed', { message: error?.message || String(error), write: 'removeResponse' });
      if (context?.prevData) {
        queryClient.setQueryData(queryKey, context.prevData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['mentalityAssessment'] });
    }
  });
}
